/**
 * The insurance card from the patient's own phone (المرحلة 1.2, P05).
 *
 * On a phone the button opens the camera straight away (`capture`); on a
 * computer it opens the file picker. The photo is shrunk in the browser before
 * it is sent, so a 12-megapixel shot travels as a few hundred kilobytes, under
 * the server's 3 MB ceiling (lib/patientFile.js MAX_CARD_BYTES).
 *
 * Whether the card counts at once is the platform's `insuranceCardSelf`
 * policy: by default it waits for the desk, as a card the desk scans does
 * today; under «trusted» a card from a contract party marked as trusting its
 * cards is accepted on arrival. The badge says which it is.
 */

import { useRef, useState } from 'react';
import { Badge, Button, DateInput, Field, Icon, Input } from '../../components/common';
import { compressImage, dataUriByteLength } from '../../domain/files';
import { useI18n } from '../../i18n/I18nProvider';

const MAX_BYTES = 3 * 1024 * 1024;

const STATUS_TONE = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  accepted: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  rejected: 'border-red-500/30 bg-red-500/10 text-red-300',
};

export function CardStatus({ status }) {
  const { t } = useI18n();
  if (!status) return null;
  return (
    <Badge className={STATUS_TONE[status] || ''} icon={status === 'accepted' ? 'shield-check' : status === 'rejected' ? 'x' : 'clock'}>
      {t(`healthProfile.card.status.${status}`)}
    </Badge>
  );
}

/**
 * @param {object}   insurance  { provider, policyNo, expiry, card, status } as GET /api/health-profile answers
 * @param {Function} onSave     called with { image?, provider, policyNo, expiry }
 * @param {boolean}  saving
 */
export function InsuranceCardCapture({ insurance = {}, onSave, saving = false, actions = null }) {
  const { t } = useI18n();
  const input = useRef(null);
  const [image, setImage] = useState(undefined); // undefined: the card on file stays
  const [form, setForm] = useState({ provider: insurance.provider || '', policyNo: insurance.policyNo || '', expiry: insurance.expiry || '' });
  const [error, setError] = useState('');
  const shown = image === undefined ? insurance.card : image;

  const pick = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    try {
      const shrunk = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.8 });
      if (dataUriByteLength(shrunk) > MAX_BYTES) {
        setError(t('healthProfile.card.tooLarge'));
        return;
      }
      setImage(shrunk);
    } catch {
      setError(t('healthProfile.card.unreadable'));
    }
  };

  const submit = () => onSave({ ...(image !== undefined ? { image } : {}), provider: form.provider.trim(), policyNo: form.policyNo.trim(), expiry: form.expiry || null });

  return (
    <div className="hp-card" data-testid="insurance-card">
      <div className="hp-card__photo">
        {shown ? (
          <img src={shown} alt={t('healthProfile.card.photoAlt')} className="hp-card__img" />
        ) : (
          <div className="hp-card__placeholder">
            <Icon name="credit-card" size={40} />
            <span>{t('healthProfile.card.none')}</span>
          </div>
        )}
        <div className="hp-card__photo-actions">
          <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="insurance-card-input" />
          <Button variant="ghost" icon="camera" onClick={() => input.current && input.current.click()}>
            {shown ? t('healthProfile.card.retake') : t('healthProfile.card.take')}
          </Button>
          {image === undefined && <CardStatus status={insurance.status} />}
        </div>
        {error && (
          <p className="hp-card__error" role="alert">
            <Icon name="alert-circle" size={13} /> {error}
          </p>
        )}
      </div>

      <div className="hp-grid">
        <Field label={t('healthProfile.card.provider')}>
          <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder={t('healthProfile.card.providerHint')} data-testid="insurance-provider" />
        </Field>
        <Field label={t('healthProfile.card.policyNo')}>
          <Input value={form.policyNo} onChange={(e) => setForm({ ...form, policyNo: e.target.value })} dir="ltr" data-testid="insurance-policy" />
        </Field>
        <Field label={t('healthProfile.card.expiry')}>
          <DateInput value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} />
        </Field>
      </div>

      <div className="hp-actions">
        <Button icon="check" onClick={submit} loading={saving} disabled={!shown && !form.provider.trim()} data-testid="insurance-save">
          {t('healthProfile.card.save')}
        </Button>
        {actions}
      </div>
      <p className="hp-note">{t('healthProfile.card.reviewNote')}</p>
    </div>
  );
}

export default InsuranceCardCapture;
